import {
  annualFreeMonths,
  formatBytes,
  PLAN_CATALOG,
  planAtLeast,
  type BillingInterval,
  type EntitlementStatus,
  type PaidPlan,
  type PlanTier,
} from '@secretario/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'wouter';
import { BackIcon } from '../components/chrome/BackIcon.js';
import { describeError } from '../lib/api/http.js';
import {
  annualHere,
  annualHint,
  canBuyHere,
  ENTITLEMENTS_KEY,
  openBillingPortal,
  otherChannel,
  otherChannelLine,
  otherChannelUrl,
  catalogPriceLine,
  priceLine,
  restorePurchases,
  salesHere,
  startCheckout,
  STORE_MISSING,
  STORE_SILENT,
  trialHere,
  useEntitlements,
  useStorePrices,
} from '../lib/plan.js';
import { isNativeShell } from '../lib/platform.js';
import { storeName, storeReady } from '../lib/storeBilling.js';
import { track } from '../lib/track.js';
import './plano.css';
import { DocLink, FinePrint } from '../components/legal/Consent.js';
import { CreditsPanel } from '../components/plan/CreditsPanel.js';

const count = (value: number | null) => (value ?? 0).toLocaleString('pt-BR');

/** O que cada plano dá, nos números do catálogo (o servidor pode ajustá-los). */
const BENEFITS: Record<PlanTier, string[]> = {
  free: [
    'Mensageiro completo e protegido',
    'Agenda, lembretes, contas e lista sem limite',
    `${count(PLAN_CATALOG.free.aiDailyLimit)} pedidos ao Secretário por dia (${count(PLAN_CATALOG.free.aiMonthlyLimit)} no mês)`,
    `Amostra única de ${PLAN_CATALOG.free.voiceSampleMinutes} min de conversa ao vivo`,
    `${PLAN_CATALOG.free.deliverySample} documento pronto de amostra`,
    `Documentos sem limite de texto e ${formatBytes(PLAN_CATALOG.free.savedFilesMb * 1024 * 1024)} para guardar fotos e PDFs`,
  ],
  basic: [
    'Tudo do Essencial',
    `${count(PLAN_CATALOG.basic.aiMonthlyLimit)} pedidos ao Secretário por mês`,
    `${PLAN_CATALOG.basic.voiceMinutesMonthly} minutos de conversa ao vivo por mês`,
    `${PLAN_CATALOG.basic.deliveriesMonthly} documentos prontos por mês`,
    `${formatBytes(PLAN_CATALOG.basic.savedFilesMb * 1024 * 1024)} para fotos e PDFs, com busca pelo que está escrito dentro`,
    'Respostas automáticas que não param no meio do dia',
  ],
  pro: [
    'Tudo do Pessoal',
    `${count(PLAN_CATALOG.pro.aiMonthlyLimit)} pedidos ao Secretário por mês`,
    `${PLAN_CATALOG.pro.voiceMinutesMonthly} minutos de conversa ao vivo por mês`,
    `${PLAN_CATALOG.pro.deliveriesMonthly} documentos prontos por mês`,
    `${formatBytes(PLAN_CATALOG.pro.savedFilesMb * 1024 * 1024)} para fotos e PDFs`,
    'Raciocínio com calma nos pedidos difíceis',
    'Notícias completas',
    'Apresente na TV e no notebook',
    'Sua IA: use a sua conta do Gemini, ChatGPT ou Claude',
  ],
};

const HEADLINE: Record<PlanTier, string> = {
  free: `Você está no ${PLAN_CATALOG.free.name}`,
  basic: `Seu plano ${PLAN_CATALOG.basic.name} está ativo`,
  pro: `Seu plano ${PLAN_CATALOG.pro.name} está ativo`,
};

function shortDate(iso: string | null): string | null {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString('pt-BR', { day: 'numeric', month: 'long' });
}

type PurchaseState = 'idle' | 'waiting' | 'done' | 'cancelled' | 'late';

/** Volta do pagamento: o plano chega por aviso do provedor, alguns segundos depois. */
function usePurchaseReturn(plan: PlanTier, refetch: () => void): PurchaseState {
  const [state, setState] = useState<PurchaseState>(() => {
    const value = new URLSearchParams(window.location.search).get('compra');
    return value === 'ok' ? 'waiting' : value === 'cancelada' ? 'cancelled' : 'idle';
  });
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;
  useEffect(() => {
    if (state !== 'waiting') return;
    if (plan !== 'free') {
      setState('done');
      track('checkout_completed', { plan });
      return;
    }
    const timer = setInterval(() => refetchRef.current(), 2000);
    const giveUp = setTimeout(() => setState('late'), 40_000);
    return () => {
      clearInterval(timer);
      clearTimeout(giveUp);
    };
  }, [state, plan]);
  return state;
}

/** "Conversa ao vivo: 12 min restantes este mês" ou, no Essencial, a amostra única. */
function voiceLine(voice: EntitlementStatus['features']['voice']) {
  const left = Math.max(0, Math.ceil(voice.remainingSeconds / 60));
  if (voice.sample) {
    return left === 0
      ? `Amostra de ${voice.limitMinutes} min ao vivo: já usada`
      : `Amostra de ${voice.limitMinutes} min ao vivo: ${left} min ${left === 1 ? 'restante' : 'restantes'}`;
  }
  if (!voice.enabled || voice.limitMinutes === 0) return null;
  return `Conversa ao vivo: ${left} de ${voice.limitMinutes} min este mês`;
}

/** "Documentos prontos: 1 de 3 este mês" ou, no Essencial, "1 de amostra". */
function documentsLine(documents: EntitlementStatus['features']['documents']) {
  if (documents.monthlyLimit === null) return null;
  if (documents.sample) {
    return documents.remaining === 0
      ? `Documento pronto de amostra: já usado`
      : `Documentos prontos: ${documents.remaining ?? documents.monthlyLimit} de amostra`;
  }
  return `Documentos prontos: ${documents.used} de ${documents.monthlyLimit} este mês`;
}

export function PlanoPage() {
  const queryClient = useQueryClient();
  const [, navigate] = useLocation();
  const entitlementsQ = useEntitlements();
  const store = useStorePrices();
  const [busy, setBusy] = useState<PaidPlan | 'portal' | 'restore' | null>(null);
  const [chosen, setChosen] = useState<BillingInterval>('month');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  useEffect(() => {
    track('plan_viewed');
  }, []);

  const data = entitlementsQ.data;
  const plan = data?.plan ?? 'free';
  const sales = salesHere(data);
  const subscription = data?.subscription ?? null;
  const remaining = data?.features.ai.remainingToday;
  const remainingMonth = data?.features.ai.remainingMonth;
  const buyable = canBuyHere();
  const native = isNativeShell();
  const inStore = storeReady();
  const elsewhere = otherChannel(subscription);
  // No app, a loja é quem diz o preço: sem ele, a compra espera a loja voltar.
  const storeSilent = native && inStore && !store.loading && (store.failed || !store.prices);
  // A loja respondeu, mas nenhum plano pago está à venda nela: mostra o valor do catálogo e avisa.
  const storeMissing =
    native &&
    inStore &&
    !store.loading &&
    !storeSilent &&
    !priceLine('basic', store.prices, 'month') &&
    !priceLine('pro', store.prices, 'month');
  const purchase = usePurchaseReturn(plan, () => void entitlementsQ.refetch());

  const shown: PlanTier[] = (['free', 'basic', 'pro'] as const).filter(
    (tier) => tier !== 'pro' || sales.pro || plan === 'pro',
  );
  const until = shortDate(subscription?.periodEnd ?? subscription?.renewsAt ?? null);
  const trialUntil = shortDate(subscription?.trialEndsAt ?? null);
  // Anual só aparece quando este canal vende e quando ainda não há assinatura de outro canal.
  const showPeriod = buyable && !elsewhere && annualHere(data, store.prices);
  const period: BillingInterval = showPeriod ? chosen : 'month';
  const gift = annualFreeMonths('basic');
  const voice = data ? voiceLine(data.features.voice) : null;
  const documents = data ? documentsLine(data.features.documents) : null;

  /** A pessoa pode trocar para este plano por aqui? */
  function canSwitchTo(tier: PaidPlan): boolean {
    if (plan === 'free') return true;
    // No app, a assinatura da loja sobe e desce (descer vale na renovação).
    if (native)
      return subscription?.source === 'google_play' || subscription?.source === 'app_store';
    // No site, só subir; para descer, cancele e assine de novo.
    return tier === 'pro';
  }

  async function buy(target: PaidPlan) {
    if (busy) return;
    setBusy(target);
    setError('');
    setNotice('');
    track('checkout_started', { plan: target, surface: 'plano' });
    try {
      const downgrade = plan !== 'free' && !planAtLeast(target, plan);
      if (await startCheckout(target, data, period)) {
        await queryClient.invalidateQueries({ queryKey: ENTITLEMENTS_KEY });
        if (downgrade && native) {
          setNotice(
            `Pronto. Na próxima renovação você passa para o ${PLAN_CATALOG[target].name}; até lá, o ${PLAN_CATALOG[plan].name} continua.`,
          );
        }
      }
      // Na web a página já saiu para o pagamento; na loja, a pessoa pode ter fechado a tela.
      setBusy(null);
    } catch (failure) {
      setError(describeError(failure));
      setBusy(null);
    }
  }

  async function manage() {
    if (busy) return;
    setBusy('portal');
    setError('');
    try {
      await openBillingPortal();
    } catch (failure) {
      setError(describeError(failure));
      setBusy(null);
    }
  }

  async function restore() {
    if (busy) return;
    setBusy('restore');
    setError('');
    try {
      await restorePurchases();
      await queryClient.invalidateQueries({ queryKey: ENTITLEMENTS_KEY });
    } catch (failure) {
      setError(describeError(failure));
    } finally {
      setBusy(null);
    }
  }

  function goBack() {
    let target = '/';
    try {
      target = window.sessionStorage.getItem('secretario.plano.volta') || '/';
      window.sessionStorage.removeItem('secretario.plano.volta');
    } catch {
      /* volta para o Início */
    }
    navigate(target.startsWith('/plano') ? '/' : target);
  }

  return (
    <div className={`pn${plan === 'pro' ? ' pro' : ''}`}>
      <div className="pn-wrap">
        <header className="pn-hd">
          <Link href="/ajustes" className="back-btn" aria-label="Voltar para Ajustes">
            <BackIcon />
          </Link>
        </header>

        <section className="pn-hero">
          <div className="pn-heroinfo">
            <span className="pn-badge">{PLAN_CATALOG[plan].name}</span>
            <h1>{HEADLINE[plan]}</h1>
            <p>
              {plan === 'free'
                ? remaining === null || remaining === undefined
                  ? `${count(PLAN_CATALOG.free.aiDailyLimit)} pedidos ao Secretário por dia.`
                  : remaining === 0
                    ? 'Seus pedidos de hoje acabaram. Amanhã cedo eles voltam.'
                    : `Hoje ainda ${remaining === 1 ? 'resta 1 pedido' : `restam ${remaining} pedidos`}.`
                : trialUntil
                  ? subscription?.cancelAtPeriodEnd
                    ? `Teste grátis até ${trialUntil}. Você cancelou: nada será cobrado.`
                    : `Teste grátis até ${trialUntil}. Depois disso a assinatura começa a ser cobrada.`
                  : subscription?.cancelAtPeriodEnd && until
                    ? `Vale até ${until}. Depois disso você volta pro ${PLAN_CATALOG.free.name}.`
                    : plan === 'basic' && !subscription
                      ? `Incluído no seu plano do Meu negócio${remainingMonth !== null && remainingMonth !== undefined ? `: neste mês ${remainingMonth === 1 ? 'resta 1 pedido' : `restam ${remainingMonth.toLocaleString('pt-BR')} pedidos`}` : ''}.`
                      : remainingMonth !== null && remainingMonth !== undefined
                        ? `Neste mês ${remainingMonth === 1 ? 'resta 1 pedido' : `restam ${remainingMonth.toLocaleString('pt-BR')} pedidos`}${until ? `. Renova em ${until}` : ''}.`
                        : until
                          ? `Renova em ${until}.`
                          : 'Seus pedidos ao Secretário estão liberados.'}
            </p>
            {(voice || documents) && (
              <ul className="pn-usage">
                {voice && <li>{voice}</li>}
                {documents && <li>{documents}</li>}
              </ul>
            )}
          </div>
        </section>

        {purchase === 'waiting' && (
          <p className="pn-flash wait" role="status">
            Pagamento recebido. Estou liberando o seu plano…
          </p>
        )}
        {purchase === 'done' && (
          <div className="pn-flash done" role="status">
            <b>Pronto. Seu plano {PLAN_CATALOG[plan].name} está ativo.</b>
            <button onClick={goBack}>Voltar pra onde eu estava</button>
          </div>
        )}
        {purchase === 'late' && (
          <p className="pn-flash wait" role="status">
            O pagamento ainda está sendo confirmado. Assim que chegar, seu plano muda sozinho.
          </p>
        )}
        {purchase === 'cancelled' && (
          <p className="pn-flash" role="status">
            O pagamento não foi concluído. Nada foi cobrado.
          </p>
        )}
        {notice && (
          <p className="pn-flash done" role="status">
            {notice}
          </p>
        )}
        {error && (
          <p className="pn-flash bad" role="alert">
            {error}
          </p>
        )}

        {showPeriod && (
          <div className="pn-period" role="radiogroup" aria-label="Período da assinatura">
            <span className={`pn-period-thumb${period === 'year' ? ' year' : ''}`} aria-hidden />
            <button
              role="radio"
              aria-checked={period === 'month'}
              className={period === 'month' ? 'on' : ''}
              onClick={() => setChosen('month')}
            >
              Mensal
            </button>
            <button
              role="radio"
              aria-checked={period === 'year'}
              className={period === 'year' ? 'on' : ''}
              onClick={() => setChosen('year')}
            >
              Anual
              {gift > 0 && (
                <small>{gift === 1 ? '1 mês de presente' : `${gift} meses de presente`}</small>
              )}
            </button>
          </div>
        )}

        <section className="pn-plans" aria-label="Planos">
          {shown.map((tier) => {
            const mine = tier === plan;
            const price = priceLine(tier, store.prices, tier === 'free' ? 'month' : period);
            const shownPrice =
              price ??
              (storeMissing ? catalogPriceLine(tier, tier === 'free' ? 'month' : period) : null);
            const hint =
              tier !== 'free' && period === 'year' ? annualHint(tier, store.prices) : null;
            // No app, a loja só confirma o teste do mensal; na web vale nos dois períodos.
            const trialDays =
              tier !== 'free' && !mine && (!native || period === 'month')
                ? trialHere(tier, data, store.prices)
                : 0;
            const monthly = priceLine(tier, store.prices, 'month');
            const canBuy =
              tier !== 'free' &&
              !mine &&
              sales[tier] &&
              buyable &&
              !elsewhere &&
              canSwitchTo(tier) &&
              (!native || !!price);
            const downgrade = tier !== 'free' && plan !== 'free' && !planAtLeast(tier, plan);
            return (
              <article key={tier} className={`pn-plan ${tier}${mine ? ' mine' : ''}`}>
                <div className="pn-plan-hd">
                  <b>{PLAN_CATALOG[tier].name}</b>
                  {mine && <span className="pn-mine">Seu plano</span>}
                  {!mine && trialDays > 0 && (
                    <span className="pn-trial">{trialDays} dias grátis</span>
                  )}
                </div>
                {shownPrice && (
                  <div className="pn-price" key={`${tier}-${period}`}>
                    {shownPrice}
                  </div>
                )}
                {hint && <div className="pn-price-sub">{hint}</div>}
                <ul>
                  {BENEFITS[tier].map((benefit) => (
                    <li key={benefit}>{benefit}</li>
                  ))}
                </ul>
                {canBuy && (
                  <button
                    className="pn-cta"
                    onClick={() => void buy(tier)}
                    disabled={busy !== null}
                  >
                    {busy === tier
                      ? 'Abrindo o pagamento…'
                      : downgrade
                        ? `Mudar para o ${PLAN_CATALOG[tier].name}`
                        : trialDays > 0
                          ? `Começar ${trialDays} dias grátis`
                          : `Assinar o ${PLAN_CATALOG[tier].name}`}
                  </button>
                )}
                {canBuy && trialDays > 0 && (
                  <p className="pn-plan-note">
                    Depois, {(period === 'year' ? price : monthly) ?? ''}. Cancele antes do fim do
                    teste e nada é cobrado.
                  </p>
                )}
                {canBuy && downgrade && (
                  <p className="pn-plan-note">A troca vale na próxima renovação.</p>
                )}
              </article>
            );
          })}
        </section>
        {(storeSilent || storeMissing) && (
          <p className="pn-foot" role="status">
            {storeSilent ? STORE_SILENT : STORE_MISSING}
          </p>
        )}

        <CreditsPanel surface="plano" />

        {elsewhere && (
          <p className="pn-foot">
            {otherChannelLine(elsewhere)}{' '}
            <a href={otherChannelUrl(elsewhere)} target="_blank" rel="noreferrer">
              {elsewhere === 'web' ? 'Abrir o site' : 'Abrir as assinaturas da loja'}
            </a>
          </p>
        )}
        {subscription?.source === 'web' && !native && (
          <button className="pn-manage" onClick={() => void manage()} disabled={busy !== null}>
            {busy === 'portal' ? 'Abrindo…' : 'Cartão, recibos e cancelamento'}
          </button>
        )}
        {native &&
          inStore &&
          subscription &&
          subscription.source !== 'web' &&
          subscription.source !== 'grant' && (
            <button className="pn-manage" onClick={() => void manage()} disabled={busy !== null}>
              {busy === 'portal' ? 'Abrindo…' : `Gerenciar ou cancelar na ${storeName()}`}
            </button>
          )}
        {subscription &&
          subscription.source !== 'web' &&
          subscription.source !== 'grant' &&
          !inStore &&
          !elsewhere && (
            <p className="pn-foot">Sua assinatura é gerenciada nas configurações da loja.</p>
          )}
        {native && inStore && (
          <>
            <FinePrint
              className="pn-foot"
              details={
                <p>
                  O valor é cobrado na sua conta da {storeName()} na confirmação da compra (ou no
                  fim do teste grátis, quando houver) e a assinatura se renova{' '}
                  {period === 'year' ? 'todo ano' : 'todo mês'} até você cancelar nas assinaturas da
                  loja,{' '}
                  {storeName() === 'App Store'
                    ? 'até 24 horas antes da renovação.'
                    : 'antes da data de renovação.'}{' '}
                  Na primeira assinatura, você pode desistir em até 7 dias e receber tudo de volta.
                </p>
              }
            >
              {period === 'year' ? 'Anual' : 'Mensal'}, renova sozinho. Cancele quando quiser.{' '}
              <DocLink doc="termos">Termos</DocLink> e{' '}
              <DocLink doc="privacidade">Privacidade</DocLink>.
            </FinePrint>
            <button
              className="pn-manage ghost"
              disabled={busy !== null}
              onClick={() => void restore()}
            >
              {busy === 'restore' ? 'Conferindo na loja…' : 'Restaurar compras'}
            </button>
          </>
        )}
        {!native && buyable && (sales.basic || sales.pro) && (
          <FinePrint
            className="pn-foot"
            details={
              <p>
                No teste grátis, o cartão fica registrado e nada é cobrado até o fim dele; cancele
                antes e não paga nada. A assinatura se renova{' '}
                {period === 'year' ? 'todo ano' : 'todo mês'} até você cancelar aqui em Meu plano,
                sem multa, e o acesso segue até o fim do período pago. Na primeira assinatura, você
                pode desistir em até 7 dias e receber tudo de volta.
              </p>
            }
          >
            {period === 'year' ? 'Anual' : 'Mensal'}, renova sozinho. Cancele quando quiser.{' '}
            <DocLink doc="termos">Termos</DocLink> e{' '}
            <DocLink doc="privacidade">Privacidade</DocLink>.
          </FinePrint>
        )}
        {plan === 'free' && (!buyable || !sales.basic) && !storeSilent && (
          <p className="pn-foot">A assinatura ainda não está disponível por aqui.</p>
        )}

        <p className="pn-foot">
          <b>O que conta como pedido?</b> Cada vez que você pede algo ao Secretário, por voz ou
          texto, e cada resposta que ele dá numa conversa. Conversar com pessoas não conta e não tem
          limite.
        </p>
      </div>
    </div>
  );
}
